// src/helpers/storage.ts
import secureLocalStorage from "react-secure-storage";

export const storageKey = {
  sidebarDetails: "sidebar@agentCRM",
  roleId: "roleId@agentCRM",
  user: "user@agentCRM",
  token: "token@agentCRM",
  refreshToken: "refreshToken@agentCRM",
  email: "userEmail@agentCRM",
} as const;

// ---- Generic helpers (optional) ----
const isBrowser = typeof window !== "undefined";

function setItem<T = unknown>(key: string, value: T) {
  if (!isBrowser) return;
  secureLocalStorage.setItem(key, value as any);
}
function getItem<T = unknown>(key: string): T | null {
  if (!isBrowser) return null;
  const val = secureLocalStorage.getItem(key);
  return (val as T) ?? null;
}
function removeItem(key: string) {
  if (!isBrowser) return;
  secureLocalStorage.removeItem(key);
}

// ---- Sidebar ----
export function setSidebarDetails(data: unknown) {
  return setItem(storageKey.sidebarDetails, data);
}
export function getSidebarDetails() {
  return getItem(storageKey.sidebarDetails);
}

// ---- RoleId ----
export function setRoleId(data: string | number) {
  return setItem(storageKey.roleId, data);
}
export function getRoleId() {
  return getItem<string | number>(storageKey.roleId);
}

// ---- User ----
export type StoredUser = Record<string, any>;
export function setUser(data: StoredUser) {
  return setItem(storageKey.user, data);
}
export function getUser(): StoredUser | null {
  const user = getItem<StoredUser | string>(storageKey.user);
  // react-secure-storage returns what you put in; guard string edge-cases:
  if (user && typeof user === "object") return user;
  return null;
}

// ---- Token ----
export function setToken(token: string) {
  return setItem(storageKey.token, token);
}
export function getToken(): string | null {
  return getItem<string>(storageKey.token);
}

export function removeToken() {
  removeItem(storageKey.token);
}

// ---- Refresh Token ----
export function setRefreshToken(token: string) {
  return setItem(storageKey.refreshToken, token);
}
export function getRefreshToken(): string | null {
  return getItem<string>(storageKey.refreshToken);
}
export function removeRefreshToken() {
  removeItem(storageKey.refreshToken);
}

// ---- Email ----
export function setUserEmail(email: string) {
  return setItem(storageKey.email, email);
}
export function getUserEmail(): string {
  const data = getItem<string | null>(storageKey.email);
  return data ?? "";
}
export function removeUserEmail() {
  removeItem(storageKey.email);
}

// ---- Clear all (scoped to secure storage) ----
export function clearAll() {
  if (!isBrowser) return;
  secureLocalStorage.clear();
}



export function removeUser() {
    removeItem(storageKey.user);
  }
  export function removeRoleId() {
    removeItem(storageKey.roleId);
  }
  export function removeSidebarDetails() {
    removeItem(storageKey.sidebarDetails);
  }
  
  // Optional: one-call helper
  export function clearAuthArtifacts() {
    removeToken();
    removeRefreshToken();
    removeUserEmail();
    removeUser();
    removeRoleId();
    removeSidebarDetails();
  }